import { useEffect, useMemo, useRef, useState } from 'react';
import { Icon } from '../components/Icons';
import { PageHeader, Surface } from '../components/Primitives';
import {
  formatInterestDate,
  interestErrorMessage,
  listPublicInterest,
  updatePublicInterestStatus,
} from '../lib/interest';
import type { PublicInterestRecord, PublicInterestStatus } from '../types';

const STATUS_LABELS: Record<PublicInterestStatus, string> = {
  new: 'New', contacted: 'Contacted', interested: 'Interested', closed: 'Closed',
};
const STATUSES = Object.keys(STATUS_LABELS) as PublicInterestStatus[];

export function Interest() {
  const [records, setRecords] = useState<PublicInterestRecord[]>([]);
  const [nextCursor, setNextCursor] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<'all' | PublicInterestStatus>('all');
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [statusDraft, setStatusDraft] = useState<PublicInterestStatus>('new');
  const [saving, setSaving] = useState(false);
  const [success, setSuccess] = useState<string | null>(null);
  const savingRef = useRef(false);

  const load = async (cursor?: string) => {
    cursor ? setLoadingMore(true) : setLoading(true);
    setError(null);
    try {
      const result = await listPublicInterest(cursor);
      setRecords((current) => cursor
        ? [...current, ...result.interest.filter((item) => !current.some((existing) => existing.interestId === item.interestId))]
        : result.interest);
      setNextCursor(result.nextCursor);
    } catch (loadError) {
      setError(interestErrorMessage(loadError, 'list'));
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  };

  useEffect(() => { void load(); }, []);
  const selected = records.find((record) => record.interestId === selectedId) ?? null;
  useEffect(() => { if (selected) setStatusDraft(selected.status); }, [selected?.interestId, selected?.status]);

  const visible = useMemo(() => {
    const query = search.trim().toLowerCase();
    return records.filter((record) => {
      const matchesStatus = statusFilter === 'all' || record.status === statusFilter;
      const matchesQuery = !query || `${record.name} ${record.email} ${record.zip}`.toLowerCase().includes(query);
      return matchesStatus && matchesQuery;
    });
  }, [records, search, statusFilter]);

  const saveStatus = async () => {
    if (!selected || savingRef.current || statusDraft === selected.status) return;
    savingRef.current = true;
    setSaving(true); setError(null); setSuccess(null);
    try {
      const updated = await updatePublicInterestStatus(selected.interestId, statusDraft);
      setRecords((current) => current.map((record) => record.interestId === updated.interestId ? updated : record));
      setSuccess(`Status changed to ${STATUS_LABELS[updated.status]}.`);
    } catch (updateError) {
      setError(interestErrorMessage(updateError, 'update'));
    } finally {
      savingRef.current = false;
      setSaving(false);
    }
  };

  return <>
    <PageHeader title="Interest" description="People who asked to be kept informed about Compass." action={<span className="count-pill">{records.length}{nextCursor ? '+' : ''} records</span>} />
    {error && <div className="notice interest-notice" role="alert">{error}</div>}
    {success && <div className="feedback success interest-feedback" role="status"><Icon name="shield" />{success}</div>}
    <div className="interest-layout">
      <Surface title="Public interest" description="Keep Me Posted submissions from compassgo.app" className="interest-list-surface">
        <div className="interest-controls">
          <label className="interest-search"><span className="sr-only">Search loaded Interest records</span><Icon name="search"/><input value={search} onChange={(event) => setSearch(event.target.value)} placeholder="Search name, email, or ZIP" /></label>
          <label className="interest-filter"><span>Status</span><select value={statusFilter} onChange={(event) => setStatusFilter(event.target.value as typeof statusFilter)}><option value="all">All statuses</option>{STATUSES.map((status) => <option value={status} key={status}>{STATUS_LABELS[status]}</option>)}</select></label>
        </div>
        {loading ? <div className="loading-row">Loading Interest records…</div> : records.length === 0 && error ? <div className="interest-empty"><h3>Interest is unavailable</h3><button className="secondary compact-button" onClick={() => void load()}>Try again</button></div> : records.length === 0 ? <div className="interest-empty"><span className="empty-icon"><Icon name="interest" /></span><h3>No Interest records yet</h3><p>New Keep Me Posted submissions will appear here.</p></div> : visible.length === 0 ? <div className="interest-empty compact"><h3>No matching records</h3><p>Try a different search or status.</p></div> : <div className="interest-table" role="table" aria-label="Public Interest records">
          <div className="interest-table-head" role="row"><span>Name</span><span>Email</span><span>ZIP</span><span>Submitted</span><span>Status</span></div>
          {visible.map((record) => <button type="button" className={`interest-row ${selectedId === record.interestId ? 'selected' : ''}`} key={record.interestId} onClick={() => { setSelectedId(record.interestId); setSuccess(null); }} aria-label={`Open ${record.name}`}>
            <span data-label="Name"><strong>{record.name}</strong></span><span data-label="Email">{record.email}</span><span data-label="ZIP">{record.zip}</span><span data-label="Submitted">{formatInterestDate(record.lastSubmittedAt)}</span><span data-label="Status"><span className={`interest-status ${record.status}`}>{STATUS_LABELS[record.status]}</span></span>
          </button>)}
        </div>}
        {nextCursor && <div className="backup-pagination"><button className="secondary compact-button" disabled={loadingMore} onClick={() => void load(nextCursor)}>{loadingMore ? 'Loading…' : 'Load more'}</button></div>}
      </Surface>
      <Surface title="Interest details" description={selected ? selected.name : 'Select a person to review'} className="interest-detail-surface">
        {!selected ? <div className="interest-detail-empty"><Icon name="interest"/><p>Select an Interest record to view contact and submission details.</p></div> : <div className="interest-detail">
          <Detail label="Name">{selected.name}</Detail><Detail label="Email"><a href={`mailto:${selected.email}`}>{selected.email}</a></Detail><Detail label="Home ZIP Code">{selected.zip}</Detail><Detail label="First submitted">{formatInterestDate(selected.submittedAt)}</Detail><Detail label="Most recent submission">{formatInterestDate(selected.lastSubmittedAt)}</Detail><Detail label="Submissions">{selected.submissionCount.toLocaleString()}</Detail>
          {selected.message && <Detail label="Interest in Compass"><p className="interest-message">{selected.message}</p></Detail>}
          <div className="interest-status-form"><label htmlFor="interest-status">Status</label><select id="interest-status" value={statusDraft} onChange={(event) => setStatusDraft(event.target.value as PublicInterestStatus)}>{STATUSES.map((status) => <option value={status} key={status}>{STATUS_LABELS[status]}</option>)}</select><button className="primary compact-button" disabled={saving || statusDraft === selected.status} onClick={() => void saveStatus()}>{saving ? 'Saving…' : 'Save status'}</button></div>
          {selected.statusUpdatedAt && <p className="interest-updated">Last status change {formatInterestDate(selected.statusUpdatedAt)}</p>}
        </div>}
      </Surface>
    </div>
  </>;
}

function Detail({ label, children }: { label: string; children: React.ReactNode }) {
  return <div className="interest-detail-row"><span>{label}</span><div>{children}</div></div>;
}
